import { Controller } from "stimulus"

export default class extends Controller {
  static targets = ["prefix", "suffix"]
  static values = {
    prefix: String,
    phrases: String,
    typeSpeed: Number,
    deleteSpeed: Number,
    pauseDuration: Number,
    initialDelay: Number
  }

  connect() {
    this.prefix = this.hasPrefixValue ? this.prefixValue : ""
    const allPhrases = JSON.parse(this.phrasesValue || "[]")
    // Keep first phrase fixed, shuffle the rest
    if (allPhrases.length > 1) {
      const first = allPhrases[0]
      const rest = allPhrases.slice(1)
      this.shuffleArray(rest)
      this.phrases = [first, ...rest]
    } else {
      this.phrases = allPhrases
    }
    this.typeSpeed = this.hasTypeSpeedValue ? this.typeSpeedValue : 20
    this.deleteSpeed = this.hasDeleteSpeedValue ? this.deleteSpeedValue : 20
    this.pauseDuration = this.hasPauseDurationValue ? this.pauseDurationValue : 2000
    this.initialDelay = this.hasInitialDelayValue ? this.initialDelayValue : 500

    if (this.phrases.length === 0) return

    this.currentPhraseIndex = 0
    this.currentCharIndex = 0
    this.isDeleting = false
    this.prefixComplete = false

    // Clear initial text
    if (this.hasPrefixTarget) this.prefixTarget.textContent = ""
    this.suffixTarget.textContent = ""
    this.suffixTarget.style.visibility = "visible"

    // Create cursor element (streaming dot)
    this.cursor = document.createElement("span")
    this.cursor.className = "streaming-cursor"

    // Start cursor after prefix target
    if (this.hasPrefixTarget) {
      this.prefixTarget.after(this.cursor)
    } else {
      this.suffixTarget.after(this.cursor)
    }

    setTimeout(() => {
      this.type()
    }, this.initialDelay)
  }

  shuffleArray(array) {
    for (let i = array.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [array[i], array[j]] = [array[j], array[i]]
    }
  }

  disconnect() {
    if (this.timeout) {
      clearTimeout(this.timeout)
    }
    if (this.cursor && this.cursor.parentNode) {
      this.cursor.remove()
    }
  }

  type() {
    // First, type the prefix if not complete
    if (!this.prefixComplete && this.hasPrefixTarget) {
      if (this.currentCharIndex < this.prefix.length) {
        this.currentCharIndex++
        this.prefixTarget.textContent = this.prefix.substring(0, this.currentCharIndex)
        this.timeout = setTimeout(() => this.type(), this.typeSpeed)
        return
      } else {
        this.prefixComplete = true
        this.currentCharIndex = 0
        // Move cursor to after suffix
        this.suffixTarget.after(this.cursor)
      }
    }

    const currentSuffix = this.phrases[this.currentPhraseIndex]

    if (this.isDeleting) {
      // Delete suffix characters
      this.currentCharIndex--
      this.suffixTarget.textContent = currentSuffix.substring(0, this.currentCharIndex)

      if (this.currentCharIndex === 0) {
        // Finished deleting, move to next phrase
        this.isDeleting = false
        this.currentPhraseIndex = (this.currentPhraseIndex + 1) % this.phrases.length
        this.timeout = setTimeout(() => this.type(), 300)
      } else {
        this.timeout = setTimeout(() => this.type(), this.deleteSpeed)
      }
    } else {
      // Type suffix characters
      this.currentCharIndex++
      this.suffixTarget.textContent = currentSuffix.substring(0, this.currentCharIndex)

      if (this.currentCharIndex === currentSuffix.length) {
        // Finished typing
        if (this.phrases.length === 1) {
          // Single phrase - fade out cursor and dispatch event
          setTimeout(() => {
            this.cursor.style.opacity = "0"
            this.cursor.style.transition = "opacity 0.3s ease"
            this.element.dispatchEvent(new CustomEvent("typing:complete", { bubbles: true }))
          }, 500)
        } else {
          // Multiple phrases - pause then delete
          this.isDeleting = true
          this.timeout = setTimeout(() => this.type(), this.pauseDuration)
        }
      } else {
        this.timeout = setTimeout(() => this.type(), this.typeSpeed)
      }
    }
  }
}
